import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const CTASectionDesktop = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "content_centered"

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white (#FFF) if toggle is off or not set
    return "#FFF"
  }

  const backgroundColor = resolveSectionBackgroundColor()
  // Get highlights from settings with fallback to legacy highlights column
  const highlights = settings.highlights || section.highlights || []

  const renderContent = (textAlign = "center") => {
    // Use theme colors (removed custom color pickers)
    const titleColor = theme?.colors?.textDark || "#000000"
    const subheadlineColor = theme?.colors?.textDark || "#000000"
    const descriptionColor = theme?.colors?.textDark || "#000000"

    return (
      <div className="space-y-4">
        {/* Headline and subheadline use textAlign */}
        <div className={`text-${textAlign}`}>
          {section.headline && (
            <h2
              className="text-[40px] font-bold leading-[48px]"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: titleColor
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="text-[27px] leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: subheadlineColor
              }}>
              {section.subheadline}
            </p>
          )}
        </div>
        {/* Body content - let WYSIWYG classes control alignment */}
        {section.body && (
          <>
            <style>{`
              .cta-section-content {
                text-align: initial !important;
              }
              .cta-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-section-content .ql-align-right {
                text-align: right !important;
              }
              .cta-section-content .ql-align-center {
                text-align: center !important;
              }
              .cta-section-content .ql-align-justify {
                text-align: justify !important;
              }
              .cta-section-content .ql-align-left {
                text-align: left !important;
              }
              .cta-section-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .cta-section-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .cta-section-content p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              .cta-section-content p:first-child {
                margin-top: 0 !important;
              }
              .cta-section-content p:last-child {
                margin-bottom: 0 !important;
              }
              .cta-section-content strong,
              .cta-section-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .cta-section-content em,
              .cta-section-content i {
                font-style: italic !important;
              }
              .cta-section-content u {
                text-decoration: underline !important;
              }
              .cta-section-content h1 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 56px !important;
                line-height: 72px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              .cta-section-content h2 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              .cta-section-content h3 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 27px !important;
                line-height: 40px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${descriptionColor} !important;
              }
              .cta-section-content h4 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 20px !important;
                line-height: 32px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${descriptionColor} !important;
              }
              .cta-section-content h5 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${descriptionColor} !important;
              }
              .cta-section-content h6 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${descriptionColor} !important;
              }
              @media (max-width: 768px) {
                .cta-section-content h1 {
                  font-size: 32px !important;
                  line-height: 48px !important;
                }
                .cta-section-content h2 {
                  font-size: 27px !important;
                  line-height: 32px !important;
                }
                .cta-section-content h3 {
                  font-size: 22px !important;
                  line-height: 32px !important;
                }
                .cta-section-content h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                .cta-section-content h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                .cta-section-content h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
            <div
              className="cta-section-content prose prose-lg mx-auto max-w-none text-[20px] leading-[28px]" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: descriptionColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>
    )
  }

  const renderHighlight = (highlight, index) => {
    // Use theme colors (removed custom color pickers)
    const titleColor = theme?.colors?.textDark || "#000000"
    const descriptionColor = theme?.colors?.textDark || "#000000"
    const highlightTitleFontSize = settings.highlightTitleFontSize || "18px"
    const highlightDescriptionFontSize = settings.highlightDescriptionFontSize || "14px"

    return (
      <div key={index} className="flex flex-col items-center space-y-3 text-center md:space-y-4">
        {highlight.imageUrl && (
          <div className="mb-2 md:mb-4">
            <img
              src={highlight.imageUrl}
              alt={highlight.title || `Highlight ${index + 1}`}
              className={twMerge("h-24 w-24 object-cover shadow-lg md:h-32 md:w-32", getThemeShapeClasses(theme))}
            />
          </div>
        )}
        {highlight.title && (
          <h3
            className="font-semibold leading-[24px] md:text-xl"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: titleColor,
              fontSize: highlightTitleFontSize
            }}>
            {highlight.title}
          </h3>
        )}
        {highlight.description && (
          <>
            <style>{`
              .cta-highlight-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-highlight-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-highlight-content .ql-align-right {
                text-align: right !important;
              }
              .cta-highlight-content .ql-align-center {
                text-align: center !important;
              }
              .cta-highlight-content .ql-align-justify {
                text-align: justify !important;
              }
              .cta-highlight-content .ql-align-left {
                text-align: left !important;
              }
              .cta-highlight-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .cta-highlight-content p {
                margin-bottom: 0.75rem !important;
                margin-top: 0 !important;
              }
              .cta-highlight-content p:first-child {
                margin-top: 0 !important;
              }
              .cta-highlight-content p:last-child {
                margin-bottom: 0 !important;
              }
              .cta-highlight-content strong,
              .cta-highlight-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .cta-highlight-content em,
              .cta-highlight-content i {
                font-style: italic !important;
              }
              .cta-highlight-content u {
                text-decoration: underline !important;
              }
              .cta-highlight-content h1, .cta-highlight-content h2, .cta-highlight-content h3,
              .cta-highlight-content h4, .cta-highlight-content h5, .cta-highlight-content h6 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                color: ${descriptionColor} !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              .cta-highlight-content h1 {
                font-size: 32px !important;
                line-height: 48px !important;
              }
              .cta-highlight-content h2 {
                font-size: 27px !important;
                line-height: 32px !important;
              }
              .cta-highlight-content h3 {
                font-size: 22px !important;
                line-height: 32px !important;
              }
              .cta-highlight-content h4 {
                font-size: 18px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h5 {
                font-size: 16px !important;
                line-height: 24px !important;
              }
              .cta-highlight-content h6 {
                font-size: 14px !important;
                line-height: 20px !important;
              }
            `}</style>
            <div
              className="cta-highlight-content leading-[20px] md:text-base" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: descriptionColor,
                fontSize: highlightDescriptionFontSize
              }}
              dangerouslySetInnerHTML={{
                __html: typeof highlight.description === "object" ? highlight.description.html : highlight.description
              }}
            />
          </>
        )}
      </div>
    )
  }

  // Layout-specific rendering
  switch (layout) {
    case "content_centered":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection>
              {section.imageUrl && (
                <div className="mb-8 flex justify-center">
                  <div className="relative">
                    <img
                      src={section.imageUrl}
                      alt={section.headline || "CTA image"}
                      className={twMerge("relative h-64 w-64 object-cover", getThemeShapeClasses(theme))}
                    />
                  </div>
                </div>
              )}
              {renderContent("left")}
            </AnimatedSection>
          </div>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-row items-center">
              <AnimatedSection className="flex-1 pr-6">{renderContent("left")}</AnimatedSection>
              {section.imageUrl && (
                <div className="relative flex-1">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "image_left":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-row items-center">
              {section.imageUrl && (
                <div className="relative flex-1">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
              <AnimatedSection className="flex-1 pl-6">{renderContent("left")}</AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "half_image_right":
      return (
        <section
          className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto min-h-[624px] max-w-6xl px-4">
            <div className="relative flex flex-row items-center">
              <AnimatedSection
                className={twMerge("relative", getThemeShapeClasses(theme))}
                style={{
                  backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
                  width: "680px",
                  maxHeight: "450px",
                  marginTop: "10rem",
                  marginRight: "-15rem",
                  zIndex: 10
                }}>
                <div className="max-w-2xl p-8">{renderContent("left")}</div>
              </AnimatedSection>
              {section.imageUrl && (
                <div className="relative">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-full w-full object-cover shadow-xl", getThemeShapeClasses(theme))}
                    style={{
                      minWidth: "680px",
                      minHeight: "450px",
                      maxWidth: "680px",
                      maxHeight: "450px",
                      objectFit: "cover"
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "half_image_left":
      return (
        <section
          className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto min-h-[624px] max-w-6xl px-4">
            <div className="relative flex flex-row items-center">
              {section.imageUrl && (
                <div className="relative">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-full w-full object-cover shadow-xl", getThemeShapeClasses(theme))}
                    style={{
                      minWidth: "680px",
                      minHeight: "450px",
                      maxWidth: "680px",
                      maxHeight: "450px",
                      objectFit: "cover"
                    }}
                  />
                </div>
              )}
              <AnimatedSection
                className={twMerge("relative", getThemeShapeClasses(theme))}
                style={{
                  backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
                  width: "680px",
                  maxHeight: "450px",
                  marginTop: "10rem",
                  marginLeft: "-15rem",
                  zIndex: 10
                }}>
                <div className="max-w-2xl p-8">{renderContent("left")}</div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "highlights_2":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection className="mb-8 md:mb-12">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-2 gap-6 lg:gap-8">
              {highlights.slice(0, 2).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    case "highlights_3":
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection className="mb-8 md:mb-12">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-3 gap-6 lg:gap-8">
              {highlights.slice(0, 3).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    default:
      return (
        <section
          className={twMerge("relative py-12 md:py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection>{renderContent("center")}</AnimatedSection>
          </div>
        </section>
      )
  }
}

export default CTASectionDesktop
